import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

interface CopyButtonProps {
    text: string;
    size?: number;
    /** 用于无障碍朗读，例如 "Copy transaction hash"。 */
    label?: string;
}

/** 兼容非安全上下文（http）的剪贴板写入兜底实现。 */
const fallbackCopy = (text: string): boolean => {
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        ta.remove();
        return ok;
    } catch {
        return false;
    }
};

const CopyButton: React.FC<CopyButtonProps> = ({ text, size = 14, label = 'Copy to clipboard' }) => {
    const [copied, setCopied] = useState(false);
    const timerRef = useRef<number | null>(null);

    useEffect(
        () => () => {
            if (timerRef.current != null) window.clearTimeout(timerRef.current);
        },
        [],
    );

    const handleCopy = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        let ok: boolean;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                ok = true;
            } else {
                ok = fallbackCopy(text);
            }
        } catch (err) {
            console.error('Failed to copy text:', err);
            ok = fallbackCopy(text);
        }
        if (!ok) return;
        setCopied(true);
        if (timerRef.current != null) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            type="button"
            onClick={handleCopy}
            aria-label={label}
            title={label}
            style={{
                color: copied ? 'var(--app-up)' : 'var(--app-text-secondary)',
                display: 'inline-flex',
                alignItems: 'center',
                padding: 'var(--space-xxs)',
                transition: 'color 0.2s',
                marginLeft: 'var(--space-xs)',
            }}
        >
            {copied ? <Check size={size} /> : <Copy size={size} />}
        </button>
    );
};

export default CopyButton;
